import React, { useState } from "react";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";
import { HandHeart } from "lucide-react";

const PRESETS = [5, 10, 25, 50];

export default function DonationWidget({ memorialSlug, className = "" }) {
  const [amount, setAmount] = useState(10);
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);

  const chosen = () => {
    const n = custom ? parseFloat(custom) : amount;
    return Number.isFinite(n) && n >= 1 ? n : null;
  };

  const donate = async () => {
    const value = chosen();
    if (!value) {
      toast.error("Please choose or enter an amount ($1 minimum).");
      return;
    }
    setBusy(true);
    try {
      const { data } = await apiClient.post("/paypal/checkout/donation", {
        amount_usd: value,
        memorial_slug: memorialSlug || null,
        origin_url: window.location.origin,
      });
      window.location.href = data.approval_url;
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Could not start PayPal.");
      setBusy(false);
    }
  };

  return (
    <div className={`glass rounded-sm p-8 ${className}`} data-testid="donation-widget">
      <div className="flex items-start gap-3 mb-5">
        <HandHeart size={22} className="text-[var(--rb-gold)] mt-1 shrink-0" strokeWidth={1.5} />
        <div>
          <div className="tracking-celestial text-[var(--rb-gold)] mb-1">A small gift, if you&apos;re moved</div>
          <p className="font-serif-display italic text-[14px] text-[var(--rb-text-body)] leading-relaxed">
            The Heirloom Pet is built and cared for by hand. Any gift, however small, helps keep
            this garden growing for the families who need it.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2 mb-3">
        {PRESETS.map((n) => (
          <button
            key={n}
            onClick={() => { setAmount(n); setCustom(""); }}
            className={`py-2 rounded-full text-[13px] border transition-colors ${
              (!custom && amount === n)
                ? "bg-[var(--rb-gold)] text-white border-[var(--rb-gold)]"
                : "border-[var(--rb-gold)]/40 text-[var(--rb-text-body)] hover:border-[var(--rb-gold)]"
            }`}
            data-testid={`donation-preset-${n}`}
          >
            ${n}
          </button>
        ))}
      </div>
      <input
        type="number"
        min="1"
        step="1"
        placeholder="Other amount"
        value={custom}
        onChange={(e) => setCustom(e.target.value)}
        className="rb-input mb-3"
        data-testid="donation-custom-input"
      />
      <button
        onClick={donate}
        disabled={busy || !chosen()}
        data-testid="donation-paypal-btn"
        className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#FFC439] hover:bg-[#F5B93B] text-[#003087] font-bold text-[13px] tracking-wide disabled:opacity-50 transition-colors"
      >
        <svg viewBox="0 0 100 30" width="70" height="20" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <text x="0" y="22" fontFamily="Helvetica,Arial,sans-serif" fontWeight="700" fontSize="22" fill="#003087">Pay</text>
          <text x="42" y="22" fontFamily="Helvetica,Arial,sans-serif" fontWeight="700" fontSize="22" fill="#009cde">Pal</text>
        </svg>
        <span>{busy ? "Opening…" : `Give $${chosen() || amount}`}</span>
      </button>
      <p className="text-[10px] text-[var(--rb-text-muted)] italic mt-3 text-center">
        Secure PayPal checkout · You&apos;ll return here after payment.
      </p>
    </div>
  );
}
